
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Transaction, CategoryType, CreditCard } from '../types';
import { CATEGORY_CONFIG } from '../constants';
import { maskCurrency, parseCurrencyToNumber, formatCurrency } from '../utils/helpers';
import { createCalendarReminder } from '../utils/googleCalendar';

import TypeSelector from './transaction-form/TypeSelector';
import CategorySelector from './transaction-form/CategorySelector';
import DescriptionSelector from './transaction-form/DescriptionSelector';
import PaymentModeSelector from './transaction-form/PaymentModeSelector';

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (t: Transaction | Transaction[]) => void;
  cards: CreditCard[];
  transactions: Transaction[];
  initialData?: Transaction;
}

const PRESET_DESCRIPTIONS: Record<string, string[]> = {
  'Moradia': ['Energia', 'Água', 'Aluguel', 'Internet', 'Supermercado'],
  'Transporte': ['Carro', 'Moto', 'Bicicleta', 'Bilhete Único', 'Uber'],
  'Entretenimento': ['Netflix', 'Telecine', 'Cinema', 'Globoplay', 'IPTV'],
  'Desenvolvimento': ['Escola', 'Faculdade', 'Cursos', 'Outros'],
  'Salário': ['Salário Mensal', 'Bônus', '13º Salário', 'Adiantamento'],
  'Renda Extra': ['Venda', 'Freelance', 'Reembolso', 'Presente'],
  'Pets': ['Ração', 'Veterinário', 'Banho e Tosa'],
  'Final de semana': ['Cinema', 'Shopping', 'Restaurante', 'Balada'],
  'Delivery': ['iFood', 'Rappi', 'Zé Delivery'],
  'Pessoal': ['Cabelo', 'Academia', 'Beleza', 'Roupas', 'Saúde'],
  'Cartão de Crédito': ['Anuidade', 'Taxa Administrativa', 'Juros', 'IOF'],
  'Pagamento de Fatura': ['Pagamento Total', 'Pagamento Parcial', 'Acordo']
};

const INCOME_CATEGORIES: CategoryType[] = ['Salário', 'Renda Extra', 'Outros'];
const HIDDEN_CATEGORIES: CategoryType[] = ['Compra Parcelada', 'Pagamento de Fatura', 'Investimento', 'Pagamento fixo/mês'];

const TransactionModal: React.FC<TransactionModalProps> = ({ isOpen, onClose, onAdd, cards, transactions, initialData }) => {
  const [type, setType] = useState<'income' | 'expense'>('expense');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('0,00'); 
  const [category, setCategory] = useState<CategoryType>('Outros');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [cardId, setCardId] = useState('');
  const [isCustomDescription, setIsCustomDescription] = useState(false);
  const [paymentMode, setMode] = useState<'single' | 'installment' | 'fixed'>('single');
  const [installments, setInstallments] = useState('12'); 
  const [limitError, setLimitError] = useState<string | null>(null);

  const [reminderActive, setReminderActive] = useState(false);
  const [reminderDateTime, setReminderDateTime] = useState('');

  const numericAmount = useMemo(() => parseCurrencyToNumber(amount), [amount]);
  const isBillPayment = category === 'Pagamento de Fatura';
  const isCreditCardExpense = category === 'Cartão de Crédito';
  const showCardSelector = isCreditCardExpense || isBillPayment;

  // Reset de cartão se a categoria mudar para algo que não use cartão
  useEffect(() => {
    if (!showCardSelector && cardId !== '') {
      setCardId('');
    }
    if (category !== 'Cartão de Crédito' && paymentMode === 'installment') {
      setMode('single');
    }
  }, [category, showCardSelector, paymentMode]);

  // Lógica de Sugestão Preditiva por Tipo de Lançamento
  useEffect(() => {
    if (!initialData && isOpen) {
      if (type === 'income') {
        setCategory('Salário');
        setDescription('Salário Mensal');
        setIsCustomDescription(false);
      } else {
        setCategory('Outros');
        setDescription('');
        setIsCustomDescription(false);
      }
    }
  }, [type, isOpen, initialData]);

  const availableLimitInfo = useMemo(() => {
    if (!cardId) return null;
    const selectedCard = cards.find(c => c.id === cardId);
    if (!selectedCard) return null;

    const cardDebt = transactions.filter(t => t.cardId === cardId).reduce((acc, t) => {
      if (initialData && t.id === initialData.id) return acc;
      if (t.category === 'Pagamento de Fatura') return acc - t.amount;
      return t.type === 'expense' ? acc + t.amount : acc - t.amount;
    }, 0);

    const available = selectedCard.limit - cardDebt;
    return { available, totalLimit: selectedCard.limit };
  }, [cardId, cards, transactions, initialData]);

  useEffect(() => {
    setReminderDateTime(`${date}T09:00`);
  }, [date]);

  useEffect(() => {
    if (isOpen) {
      setReminderActive(false);
      setLimitError(null);
      if (initialData) {
        setType(initialData.type);
        setCategory(initialData.category);
        setCardId(initialData.cardId || '');
        setDescription(initialData.description);
        setAmount(maskCurrency(initialData.amount));
        setDate(initialData.date);
        setIsCustomDescription(true);
      } else {
        const today = new Date().toISOString().split('T')[0];
        setType('expense');
        setCategory('Outros');
        setCardId('');
        setDescription('');
        setAmount('0,00');
        setDate(today);
        setIsCustomDescription(false);
      }
    }
  }, [isOpen, initialData]);

  const availableCategories = useMemo(() => {
    const all = Object.keys(CATEGORY_CONFIG) as CategoryType[];
    let list: CategoryType[] = [];
    if (type === 'income') {
      list = INCOME_CATEGORIES.filter(cat => !HIDDEN_CATEGORIES.includes(cat));
    } else {
      list = all.filter(cat => !INCOME_CATEGORIES.includes(cat) && !HIDDEN_CATEGORIES.includes(cat));
      if (!list.includes('Cartão de Crédito')) list.push('Cartão de Crédito');
      if (!list.includes('Outros')) list.push('Outros');
    }
    if (isBillPayment && !list.includes('Pagamento de Fatura')) list.push('Pagamento de Fatura');
    
    return [...new Set(list)];
  }, [type, isBillPayment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLimitError(null);
    
    if (!description || numericAmount <= 0) return;
    if (showCardSelector && !cardId) {
      setLimitError("Selecione qual cartão está sendo utilizado.");
      return;
    }

    if (cardId && type === 'expense' && category !== 'Pagamento de Fatura') {
      const info = availableLimitInfo;
      if (info) {
        let totalPurchaseValue = numericAmount;
        if (paymentMode === 'installment') {
          totalPurchaseValue = numericAmount; // numericAmount is already the total for installments
        } else if (paymentMode === 'fixed') {
          totalPurchaseValue = numericAmount * 12;
        }

        if (totalPurchaseValue > info.available) {
          setLimitError(`Limite Insuficiente. Este cartão possui apenas ${formatCurrency(info.available)} disponíveis.`);
          return;
        }
      }
    }

    if (reminderActive && reminderDateTime) {
      await createCalendarReminder(description, reminderDateTime, numericAmount);
    }

    const newTransactions: Transaction[] = [];
    if (paymentMode === 'installment') {
      const num = Math.max(2, parseInt(installments) || 2);
      const instAmount = numericAmount / num;
      const groupId = crypto.randomUUID().split('-')[0];
      for (let i = 0; i < num; i++) {
        const d = new Date(date + 'T12:00:00');
        d.setMonth(d.getMonth() + i);
        newTransactions.push({
          id: crypto.randomUUID(), description: `${description} (${i + 1}/${num})`,
          amount: instAmount, type, category, date: d.toISOString().split('T')[0],
          cardId: cardId || undefined, installmentId: groupId
        });
      }
    } else if (paymentMode === 'fixed') {
      const groupId = crypto.randomUUID().split('-')[0];
      const baseDate = new Date(date + 'T12:00:00');
      const year = baseDate.getFullYear();
      const day = baseDate.getDate();

      for (let i = 0; i < 12; i++) {
        // Create date for each month (0-11) of the same year
        const d = new Date(year, i, day, 12, 0, 0);
        
        // Handle month overflow (e.g., Feb 30 -> Mar 2)
        if (d.getMonth() !== i) {
          d.setDate(0); // Set to last day of intended month
        }

        newTransactions.push({
          id: crypto.randomUUID(), description, amount: numericAmount, type, category,
          date: d.toISOString().split('T')[0], cardId: cardId || undefined, installmentId: groupId
        });
      }
    } else {
      newTransactions.push({
        id: initialData?.id || crypto.randomUUID(),
        description, amount: numericAmount, type, category, date, cardId: cardId || undefined
      } as Transaction);
    }

    onAdd(newTransactions);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/60 flex items-end md:items-center justify-center z-[100] p-0 md:p-4 backdrop-blur-md transition-all duration-500">
      <div className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-t-[2rem] md:rounded-[2.5rem] p-4 md:p-6 shadow-2xl animate-in slide-in-from-bottom-10 duration-500 max-h-[98vh] overflow-y-auto hide-scrollbar border-t border-slate-100 dark:border-slate-800 relative">
        {/* Mobile Handle */}
        <div className="w-12 h-1 bg-slate-200 dark:bg-slate-800 rounded-full mx-auto mb-4 md:hidden"></div>
        
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-lg md:text-xl font-black text-slate-900 dark:text-slate-50 heading-display uppercase tracking-tight">{initialData ? 'Editar Registro' : 'Novo Registro'}</h2>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Soberania Financeira</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center justify-center active:scale-90 transition-all border border-slate-100 dark:border-slate-800"><i className="fa-solid fa-xmark text-sm"></i></button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-2.5">
          {limitError && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-2 rounded-xl flex items-start gap-3 animate-in shake duration-500">
              <div className="w-7 h-7 rounded-lg bg-red-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-red-500/20">
                <i className="fa-solid fa-triangle-exclamation text-[10px]"></i>
              </div>
              <div>
                <p className="text-[7px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest mb-0.5">Limite Insuficiente</p>
                <p className="text-[8px] font-bold text-red-700 dark:text-red-300 leading-snug">{limitError}</p>
              </div>
            </div>
          )}

          <TypeSelector type={type} setType={setType} />
          
          <div className="flex flex-col items-center justify-center py-2 bg-slate-100 dark:bg-slate-950/50 rounded-2xl border border-slate-200 dark:border-slate-800 relative overflow-hidden group shadow-inner">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
            
            {!showCardSelector && (
              <div className="mb-1 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 flex items-center gap-1 animate-in zoom-in duration-500 shadow-sm">
                <i className="fa-solid fa-money-bill-transfer text-emerald-500 text-[7px]"></i>
                <span className="text-[7px] font-black uppercase text-emerald-600 dark:text-emerald-400 tracking-[0.2em]">DINHEIRO / PIX</span>
              </div>
            )}
            {showCardSelector && (
              <div className="mb-1 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20 flex items-center gap-1 animate-in zoom-in duration-500 shadow-sm">
                <i className="fa-solid fa-credit-card text-blue-500 text-[7px]"></i>
                <span className="text-[7px] font-black uppercase text-blue-600 dark:text-blue-400 tracking-[0.2em]">CARTÃO DE CRÉDITO</span>
              </div>
            )}
            <div className="flex items-baseline justify-center gap-1 relative z-10">
              <span className="text-lg font-black text-slate-500 heading-display">R$</span>
              <input required autoFocus type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(maskCurrency(e.target.value))} className="bg-transparent border-none p-0 text-slate-900 dark:text-slate-50 font-mono text-3xl md:text-4xl font-black outline-none focus:ring-0 text-center w-auto min-w-[80px] tracking-tighter" style={{ width: `${Math.max(amount.length + 0.5, 4)}ch` }} />
            </div>
            <label className="text-[7px] font-black text-slate-500 dark:text-slate-500 uppercase tracking-[0.3em] mt-0.5 relative z-10">Valor do lançamento</label>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <CategorySelector 
              category={category} 
              setCategory={setCategory} 
              availableCategories={availableCategories} 
            />
            <div className="space-y-1">
              <label className="block text-[9px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] ml-1">Data</label>
              <div className="relative">
                <input 
                  type="date" 
                  value={date} 
                  onChange={(e) => setDate(e.target.value)} 
                  className="w-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-800 dark:text-slate-100 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all appearance-none shadow-sm" 
                />
                <i className="fa-solid fa-calendar absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-[9px]"></i>
              </div>
            </div>
          </div>

          <div className={`grid gap-3 items-end transition-all duration-500 ${showCardSelector ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <DescriptionSelector 
              category={category} 
              description={description} 
              setDescription={setDescription} 
              isCustom={isCustomDescription} 
              setIsCustom={setIsCustomDescription} 
              presets={PRESET_DESCRIPTIONS[category] || []} 
            />
            
            {showCardSelector && (
              <div className="space-y-1 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="flex items-center justify-between ml-1">
                  <label className="block text-[9px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">Qual Cartão?</label>
                  {availableLimitInfo && (
                    <span className={`text-[7px] font-black uppercase tracking-widest ${availableLimitInfo.available < numericAmount ? 'text-red-500' : 'text-emerald-500'}`}>
                      {formatCurrency(availableLimitInfo.available)}
                    </span>
                  )}
                </div>
                <div className="relative">
                  <select required value={cardId} onChange={(e) => setCardId(e.target.value)} className="w-full bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2.5 text-xs font-bold text-slate-800 dark:text-slate-100 outline-none focus:ring-4 focus:ring-blue-500/10 transition-all appearance-none cursor-pointer shadow-sm">
                    <option value="" disabled>Selecionar...</option>
                    {cards.map(card => <option key={card.id} value={card.id}>{card.name}</option>)}
                  </select>
                  <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none text-[9px]"></i>
                </div>
              </div>
            )}
          </div>

          <PaymentModeSelector mode={paymentMode} setMode={setMode} installments={installments} setInstallments={setInstallments} showRecurrence={!initialData && !isBillPayment} isCreditCard={category === 'Cartão de Crédito'} />
          
          <div className={`p-3 rounded-xl border-2 transition-all duration-500 ${reminderActive ? 'bg-blue-600 border-blue-400 shadow-lg shadow-blue-500/20' : 'bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800 shadow-sm'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${reminderActive ? 'bg-white text-blue-600 shadow-md' : 'bg-white dark:bg-slate-700 text-slate-400 shadow-sm'}`}>
                  <i className="fa-solid fa-calendar-check text-sm"></i>
                </div>
                <div>
                  <p className={`text-[9px] font-black uppercase tracking-widest leading-none mb-0.5 ${reminderActive ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`}>Lembrete na Agenda</p>
                  <p className={`text-[7px] font-bold ${reminderActive ? 'text-white/60' : 'text-slate-500'}`}>Sincronização Cloud</p>
                </div>
              </div>
              <button 
                type="button" 
                onClick={() => setReminderActive(!reminderActive)}
                className={`w-10 h-6 rounded-full relative transition-all duration-300 p-0.5 ${reminderActive ? 'bg-blue-800' : 'bg-slate-200 dark:bg-slate-700'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 transform ${reminderActive ? 'translate-x-4' : 'translate-x-0'}`}></div>
              </button>
            </div>
            {reminderActive && (
              <div className="mt-3 animate-in slide-in-from-top-4 duration-500">
                <input 
                  type="datetime-local" 
                  value={reminderDateTime} 
                  onChange={(e) => setReminderDateTime(e.target.value)}
                  className="w-full bg-blue-700/30 border border-white/20 rounded-lg px-3 py-2 text-[9px] font-black text-white outline-none focus:ring-4 focus:ring-white/10 transition-all"
                />
              </div>
            )}
          </div>

          <div className="pt-1">
            <button type="submit" className={`w-full rounded-xl font-black text-white uppercase tracking-[0.2em] text-[9px] shadow-2xl active:scale-95 transition-all h-12 transform hover:-translate-y-1 ${type === 'expense' ? 'bg-slate-900 dark:bg-blue-600 shadow-blue-500/20' : 'bg-emerald-600 shadow-emerald-500/20'}`}>
              {initialData ? 'Atualizar Registro' : 'Salvar Registro'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransactionModal;
